import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Typography,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import useQueryParam from "../../hooks/useQueryParam";
import {
  deletePublicBulkEmail,
  getAccessTokenFromCode,
  getNylasDisconnectedEmails,
  publicEmailConnection,
} from "../../api/globalApi";
import BasicSelect from "../custom/BasicSelect";
import BulkEmailUploadCSVModal from "./BulkEmailUploadCSVModal";
import NewConfirmAlert from "../custom/NewConfirmAlert";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    height: 'unset !important',
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    height: 770,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },

  upLoadButton: {
    width: "180px",
    height: "36px",
    textTransform: "capitalize",
    backgroundColor: "var(--accent-color) !important",
    color: "#ffffff !important",
    fontWeight: "600 !important",
    "&:focus, &:hover": {
      color: "#ffffff !important",
      backgroundColor: "var(--accent-color) !important",
    },
  },
  connectButton: {
    backgroundColor: "var(--accent-color) !important",
    color: "#ffffff !important",
    fontWeight: "600 !important",
    width: "120px",
    height: "36px",
    textTransform: "capitalize",
    "&:focus, &:hover": {
      color: "#ffffff !important",
      backgroundColor: "var(--accent-color) !important",
    },
  },
  deleteButton: {
    backgroundColor: "var(--red) !important",
    color: "#ffffff !important",
    fontWeight: "600 !important",
    width: "120px",
    height: "36px",
    textTransform: "capitalize",
    "&:focus, &:hover": {
      color: "#ffffff !important",
      backgroundColor: "var(--red) !important",
    },
  },
}));

const IdentityCallback = () => {
  const classes = useStyles();
  const code = useQueryParam("code");
  const state = useQueryParam("state");
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalEmails, setTotalEmails] = useState(0);
  const [payLoadObject, setPayLoadObject] = useState({
    page: 0,
    limit: 10,
    status: "NOT_CONNECTED",
  });
  const [openCSVUploadModal, setOpenCSVUploadModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleChangePage = (event, newPage) => {
    setPayLoadObject((prevState) => ({
      ...prevState,
      page: newPage,
    }));
  };

  const emailStatus = {
    ACTIVE: "ACTIVE",
    NOT_CONNECTED: "NOT_CONNECTED",
    BLOCKED: "BLOCKED",
    INACTIVE: "INACTIVE",
  };

  const handleChangeRowsPerPage = (event) => {
    setPayLoadObject((prevState) => ({
      ...prevState,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  useEffect(() => {
    accessTokenFromCode();
  }, []);

  useEffect(() => {
    fetchNylasDisconnectedEmails();
  }, [payLoadObject]);

  const fetchNylasDisconnectedEmails = async () => {
    setLoading(true);

    try {
      const res = await getNylasDisconnectedEmails({
        page: payLoadObject.page + 1,
        limit: payLoadObject.limit,
        status: payLoadObject.status,
      });

      if (res && res.success && res.data) {
        setEmails(res.data);
        if (payLoadObject.page === 0) {
          setTotalEmails(res.total_count || 0);
        }
      } else {
        window.showNotification(
          "error",
          res?.message || "Failed to fetch emails."
        );
      }
    } catch (error) {
      console.error("Error fetching Nylas disconnected emails:", error);
      window.showNotification(
        "error",
        "An unexpected error occurred while fetching emails."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchNylasDisconnectedEmailsAfterCSVUploadSuccess = async () => {
    setLoading(true);

    try {
      const res = await getNylasDisconnectedEmails({
        page: 1,
        limit: payLoadObject.limit,
        status: payLoadObject.status,
      });

      if (res && res.success && res.data) {
        setEmails(res.data);
        if (payLoadObject.page === 0) {
          setTotalEmails(res.total_count || 0);
        }
      } else {
        window.showNotification(
          "error",
          res?.message || "Failed to fetch emails."
        );
      }
    } catch (error) {
      console.error("Error fetching Nylas disconnected emails:", error);
      window.showNotification(
        "error",
        "An unexpected error occurred while fetching emails."
      );
    } finally {
      setLoading(false);
    }
  };

  const accessTokenFromCode = async () => {
    if (code && state) {
      const payload = {
        code,
        client_id: state,
        redirect_uri:
          "https://insurance-billing.pypepro.com/thirdparty/identity/google",
      };

      try {
        const res = await getAccessTokenFromCode(payload);

        if (res && res.success) {
          console.log("Access Token:", res.data);
          // You might want to store it or use it here
        } else {
          window.showNotification("error", res?.message);
        }
      } catch (error) {
        window.showNotification(
          "error",
          error?.message || "An unexpected error occurred."
        );
      }
    } else {
      console.warn("Missing code or state");
    }
  };

  const handleConnect = async () => {
    try {
      const resp = await publicEmailConnection();
      if (resp && resp.success && resp.data && resp.data.url) {
        window.open(resp.data.url, "_blank");
      } else {
        window.showNotification("error", resp?.message || "Connection failed.");
      }
    } catch (error) {
      console.error("Connection error:", error);
      window.showNotification("error", "An unexpected error occurred.");
    }
  };

  const handleDelete = (id) => {
    NewConfirmAlert({
      title: "Confirm to delete",
      description: "Are you sure to delete this email?",
      cancelText: "Cancel",
      submitText: "Yes, Delete!",
      onSubmit: () => deleteApi(id),
    });
  };

  const deleteApi = async (id) => {
    if (deleting) return;
    setDeleting(true);
    try {
      const res = await deletePublicBulkEmail({ id: id });
      if (res && res.success) {
        window.showNotification("success", res.message);
        setEmails((prevEmails) =>
          prevEmails.filter((email) => email.id !== id)
        );
        setTotalEmails((prevTotal) => prevTotal - 1);
      } else {
        window.showNotification("error", res?.message);
      }
    } catch (error) {
      console.error("Error deleting email:", error);
      window.showNotification(
        "error",
        "An unexpected error occurred while deleting email."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={classes.root}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        className={classes.header}
      >
        <Typography variant="h5" className={classes.title} gutterBottom>
          Nylas Email Accounts
        </Typography>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div>
            <Button
              variant="contained"
              className={classes.upLoadButton}
              onClick={() => setOpenCSVUploadModal(true)}
            >
              Upload CSV
            </Button>
          </div>
          <div style={{ width: "200px" }}>
            <BasicSelect
              options={[
                { label: "Connected Email", value: emailStatus.ACTIVE },
                {
                  label: "Not Connected Email",
                  value: emailStatus.NOT_CONNECTED,
                },
                { label: "Blocked Email", value: emailStatus.BLOCKED },
                { label: "Inactive Email", value: emailStatus.INACTIVE },
              ]}
              value={payLoadObject.status}
              onChange={(e) => {
                setPayLoadObject((prevState) => ({
                  ...prevState,
                  status: e.target.value,
                  page: 0,
                }));
              }}
              mapping={{
                label: "label",
                value: "value",
              }}
              placeholder="Select Status"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            width: "100%",
            height: "500px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress className={classes.loader} />
        </div>
      ) : !loading && Array.isArray(emails) && emails.length > 0 ? (
        <div>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 600, fontSize: "20px" }}>
                    Email
                  </TableCell>
                  <TableCell style={{ fontWeight: 600, fontSize: "20px" }}>
                    Password
                  </TableCell>
                  {payLoadObject.status !== emailStatus.ACTIVE && (
                    <TableCell
                      style={{ width: 300, fontWeight: 600, fontSize: "20px" }}
                      align="center"
                    >
                      Action
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {emails.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.password}</TableCell>
                    {payLoadObject.status !== emailStatus.ACTIVE && (
                      <TableCell
                        style={{
                          width: 300,
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                          justifyContent: "center",
                        }}
                        align="center"
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleConnect}
                          className={classes.connectButton}
                        >
                          Connect
                        </Button>
                        {row.status === emailStatus.NOT_CONNECTED && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleDelete(row.id)}
                            className={classes.deleteButton}
                            disabled={deleting}
                          >
                            {deleting ? "Deleting..." : "Delete"}
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalEmails}
            page={payLoadObject.page}
            onPageChange={handleChangePage}
            rowsPerPage={payLoadObject.limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            height: "500px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          No email found.
        </div>
      )}

      {openCSVUploadModal && (
        <BulkEmailUploadCSVModal
          open={openCSVUploadModal}
          onClose={() => setOpenCSVUploadModal(false)}
          onSuccessUpload={() => {
            fetchNylasDisconnectedEmailsAfterCSVUploadSuccess();
          }}
        />
      )}
    </div>
  );
};

export default IdentityCallback;
